import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import {
  successAlert,
  errorAlert,
  warningAlert,
  confirmAlert,
} from "../../utils/alert";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { FaTrash, FaEdit, FaCalendarAlt, FaPaperclip, FaPlus, FaTimes, FaUndo, FaMinus, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CameraCaptureModal from "../../components/CameraCaptureModal";

const toDateKey = (value) => {
  if (!value) return "unknown-date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown-date";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSameLocalDate = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDateHeading = (dateKey) => {
  if (dateKey === "unknown-date") return "Unknown date";
  const [year, month, day] = dateKey.split("-").map(Number);
  const sectionDate = new Date(year, month - 1, day);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameLocalDate(sectionDate, today)) return "Today";
  if (isSameLocalDate(sectionDate, yesterday)) return "Yesterday";

  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(sectionDate);
};

const formatDateSubheading = (dateKey) => {
  if (dateKey === "unknown-date") return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

const formatDate = (dateString) => {
  if (!dateString) return "No date";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ManageNewsClips = () => {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heading, setHeading] = useState("");
  const [clipDate, setClipDate] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Edit Mode states
  const [editingClip, setEditingClip] = useState(null);
  const [editHeading, setEditHeading] = useState("");
  const [editClipDate, setEditClipDate] = useState("");
  const [editRemainingImages, setEditRemainingImages] = useState([]); // database paths
  const [editNewImageFiles, setEditNewImageFiles] = useState([]); // files array
  const [editNewPreviews, setEditNewPreviews] = useState([]); // URL previews of new files

  // Lightbox Viewer
  const [viewerClip, setViewerClip] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showCameraModal, setShowCameraModal] = useState(false);

  const fetchClips = async () => {
    setLoading(true);
    try {
      const data = await API.fetchAdminNewsClips();
      setClips(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch news clips", err);
      errorAlert("Error", "Failed to load news clips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClips();
  }, []);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setImageFiles(files);
      const previews = files.map((file) => URL.createObjectURL(file));
      setImagePreviews(previews);
    }
  };

  const handleAddNewFilesToEdit = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setEditNewImageFiles((prev) => [...prev, ...files]);
      const previews = files.map((file) => URL.createObjectURL(file));
      setEditNewPreviews((prev) => [...prev, ...previews]);
    }
  };

  const removeSelectedFileFromAdd = (index) => {
    setImageFiles((prev) => prev.filter((_, idx) => idx !== index));
    setImagePreviews((prev) => prev.filter((_, idx) => idx !== index));
  };

  const removeNewFileFromEdit = (index) => {
    setEditNewImageFiles((prev) => prev.filter((_, idx) => idx !== index));
    setEditNewPreviews((prev) => prev.filter((_, idx) => idx !== index));
  };

  const removeExistingFileFromEdit = (index) => {
    setEditRemainingImages((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!heading.trim()) {
      warningAlert("Missing fields", "Heading is required");
      return;
    }
    if (imageFiles.length === 0) {
      warningAlert("Missing fields", "Please select at least one news clip image");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("heading", heading.trim());
    for (let file of imageFiles) {
      formData.append("images[]", file);
    }
    if (clipDate) {
      formData.append("date", clipDate);
    }

    try {
      await API.createNewsClip(formData);
      successAlert("Success", "News clip uploaded successfully");
      setHeading("");
      setClipDate("");
      setImageFiles([]);
      setImagePreviews([]);
      // Reset input element
      const input = document.getElementById("clip-image-upload");
      if (input) input.value = "";
      fetchClips();
    } catch (err) {
      console.error("Upload failed", err);
      errorAlert("Upload Failed", err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (clip) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setEditingClip(clip);
    setEditHeading(clip.heading);
    // Format YYYY-MM-DD
    const rawDate = clip.date ? new Date(clip.date) : new Date();
    const formattedDate = !Number.isNaN(rawDate.getTime())
      ? rawDate.toISOString().split("T")[0]
      : "";
    setEditClipDate(formattedDate);
    setEditNewImageFiles([]);
    setEditNewPreviews([]);
    
    // Get existing image paths
    const paths = Array.isArray(clip.image_paths) && clip.image_paths.length > 0 
      ? clip.image_paths 
      : (clip.image_path ? [clip.image_path] : []);
    
    setEditRemainingImages(paths);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editHeading.trim()) {
      warningAlert("Missing fields", "Heading is required");
      return;
    }
    if (editRemainingImages.length === 0 && editNewImageFiles.length === 0) {
      warningAlert("Missing fields", "A news clip must contain at least one image");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("heading", editHeading.trim());
    if (editClipDate) {
      formData.append("date", editClipDate);
    }
    // Pass the remaining files to keep
    formData.append("remaining_images", JSON.stringify(editRemainingImages));
    // Pass the new files to upload
    if (editNewImageFiles.length > 0) {
      for (let file of editNewImageFiles) {
        formData.append("images[]", file);
      }
    }

    try {
      await API.updateNewsClip(editingClip.id, formData);
      successAlert("Updated", "News clip updated successfully");
      setEditingClip(null);
      fetchClips();
    } catch (err) {
      console.error("Update failed", err);
      errorAlert("Update Failed", err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmAlert({
      title: "Delete news clip?",
      text: "This action will delete the news clip and all its images from the database and disk.",
      confirmText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      await API.deleteNewsClip(id);
      successAlert("Deleted", "News clip removed successfully");
      fetchClips();
    } catch (err) {
      errorAlert("Error", "Failed to delete news clip");
    }
  };

  const openViewer = (clip) => {
    setViewerClip(clip);
    setViewerIndex(0);
    setZoom(1);
  };

  const getViewerImageSrc = () => {
    if (!viewerClip) return "";
    const paths = Array.isArray(viewerClip.image_paths) && viewerClip.image_paths.length > 0 
      ? viewerClip.image_paths 
      : (viewerClip.image_path ? [viewerClip.image_path] : []);
    
    return resolveAssetUrl(paths[viewerIndex] || "", { folder: "news-clips" });
  };

  const getViewerImagesLength = () => {
    if (!viewerClip) return 0;
    const paths = Array.isArray(viewerClip.image_paths) && viewerClip.image_paths.length > 0 
      ? viewerClip.image_paths 
      : (viewerClip.image_path ? [viewerClip.image_path] : []);
    
    return paths.length;
  };

  const nextViewerImage = (e) => {
    e.stopPropagation();
    const len = getViewerImagesLength();
    if (len <= 1) return;
    setViewerIndex((prev) => (prev + 1) % len);
    setZoom(1);
  };

  const prevViewerImage = (e) => {
    e.stopPropagation();
    const len = getViewerImagesLength();
    if (len <= 1) return;
    setViewerIndex((prev) => (prev - 1 + len) % len);
    setZoom(1);
  };

  // Group news clips by date key to display chronologically exactly like the gallery layout
  const groupedClips = useMemo(() => {
    return clips.reduce((acc, clip) => {
      const dateKey = toDateKey(clip.date);
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(clip);
      return acc;
    }, {});
  }, [clips]);

  const sortedDateKeys = useMemo(() => {
    return Object.keys(groupedClips).sort((a, b) => {
      if (a === "unknown-date") return 1;
      if (b === "unknown-date") return -1;
      return b.localeCompare(a); // Sort descending (newest first)
    });
  }, [groupedClips]);

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between border-b border-slate-200 pb-5 gap-4">
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900">Manage News Clips</h1>
              <p className="text-slate-500 mt-1">
                Upload and arrange news clippings chronologically by date, matching the layout of the media gallery.
              </p>
            </div>
            <div>
              <button
                type="button"
                onClick={() => setShowCameraModal(true)}
                className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 transition-all shadow-md flex items-center gap-2"
              >
                📸 Open Camera
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Upload/Edit Sidepane */}
            <div className="lg:col-span-1">
              {editingClip ? (
                // Edit Form
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm sticky top-6">
                  <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <FaEdit className="text-blue-600" /> Edit News Clip
                    </h2>
                    <button
                      type="button"
                      onClick={() => setEditingClip(null)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      Cancel
                    </button>
                  </div>
                  <form onSubmit={handleUpdate} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                        Heading
                      </label>
                      <input
                        type="text"
                        value={editHeading}
                        onChange={(e) => setEditHeading(e.target.value)}
                        placeholder="e.g. Rongali Ravi bags Gold"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                        Publication Date
                      </label>
                      <input
                        type="date"
                        value={editClipDate}
                        onChange={(e) => setEditClipDate(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Remaining Existing Images with Single Image Delete Overlay */}
                    {editRemainingImages.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
                          Current Images ({editRemainingImages.length})
                        </label>
                        <div className="grid grid-cols-3 gap-2 border border-slate-200 bg-slate-50 p-2.5 rounded-lg max-h-40 overflow-y-auto mb-3">
                          {editRemainingImages.map((path, i) => {
                            const src = resolveAssetUrl(path, { folder: "news-clips" });
                            return (
                              <div key={i} className="relative aspect-square rounded-md overflow-hidden bg-slate-200 border border-slate-300 group">
                                <img src={src} alt="Existing Preview" className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeExistingFileFromEdit(i)}
                                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
                                  title="Delete this image"
                                >
                                  <FaTrash className="text-red-500" size={16} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Add More Images */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                        Add More Images
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAddNewFilesToEdit}
                        className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 file:hover:bg-blue-100"
                      />
                      {editNewPreviews.length > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-2 border border-slate-200 bg-slate-50 p-2.5 rounded-lg max-h-40 overflow-y-auto">
                          {editNewPreviews.map((src, i) => (
                            <div key={i} className="relative aspect-square rounded-md overflow-hidden bg-slate-200 border border-slate-300 group">
                              <img src={src} alt="New Preview" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removeNewFileFromEdit(i)}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
                                title="Remove selection"
                              >
                                <FaTimes className="text-red-400" size={18} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                  </form>
                </div>
              ) : (
                // Add Form
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm sticky top-6">
                  <h2 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
                    <FaPaperclip className="text-blue-600" /> Add News Clip
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                        Heading / News Paper Title
                      </label>
                      <input
                        type="text"
                        value={heading}
                        onChange={(e) => setHeading(e.target.value)}
                        placeholder="e.g. Local Daily Coverage - Para Meet"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                        Publication Date (Defaults to Today)
                      </label>
                      <input
                        type="date"
                        value={clipDate}
                        onChange={(e) => setClipDate(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                        Clip Images (Hold Ctrl to select multiple)
                      </label>
                      <input
                        id="clip-image-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 file:hover:bg-blue-100"
                        required
                      />
                      {imagePreviews.length > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-2 border border-slate-200 bg-slate-50 p-2.5 rounded-lg max-h-40 overflow-y-auto">
                          {imagePreviews.map((src, i) => (
                            <div key={i} className="relative aspect-square rounded-md overflow-hidden bg-slate-200 border border-slate-300 group">
                              <img src={src} alt="Preview" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removeSelectedFileFromAdd(i)}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
                                title="Remove selection"
                              >
                                <FaTimes className="text-red-400" size={18} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={submitting || imageFiles.length === 0}
                      className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                    >
                      {submitting ? "Uploading..." : `Upload (${imageFiles.length}) Clip(s)`}
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Gallery Arrangement List (Grouped by Date) */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
                  Loading clips...
                </div>
              ) : sortedDateKeys.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
                  No news clips uploaded yet. Use the upload panel to add press clippings.
                </div>
              ) : (
                <div className="space-y-12">
                  {sortedDateKeys.map((dateKey) => (
                    <div key={dateKey}>
                      {/* Section Date Header */}
                      <div className="sticky top-0 z-10 mb-4 bg-slate-50 py-2 border-b border-slate-200 flex items-end justify-between">
                        <div>
                          <h3 className="text-base font-bold text-slate-800">
                            {formatDateHeading(dateKey)}
                          </h3>
                          <p className="text-xs text-slate-400">
                            {formatDateSubheading(dateKey)}
                          </p>
                        </div>
                      </div>

                      {/* Collage/Grid List under this date */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {groupedClips[dateKey].map((clip) => {
                          const paths = Array.isArray(clip.image_paths) && clip.image_paths.length > 0 
                            ? clip.image_paths 
                            : (clip.image_path ? [clip.image_path] : []);
                          const primarySrc = resolveAssetUrl(paths[0] || "", { folder: "news-clips" });
                          
                          return (
                            <div
                              key={clip.id}
                              className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-shadow"
                            >
                              <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden cursor-pointer" onClick={() => openViewer(clip)}>
                                <img
                                  src={primarySrc}
                                  alt={clip.heading}
                                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                                />
                                {paths.length > 1 && (
                                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                    +{paths.length - 1}
                                  </div>
                                )}
                              </div>
                              <div className="p-3 flex-grow flex flex-col justify-between">
                                <div>
                                  <h3 className="font-bold text-slate-950 text-xs sm:text-sm line-clamp-2" title={clip.heading}>
                                    {clip.heading}
                                  </h3>
                                  <p className="text-[10px] sm:text-xs text-slate-500 mt-1 flex items-center gap-1 font-medium">
                                    <FaCalendarAlt className="text-slate-400" />
                                    {formatDate(clip.date)}
                                  </p>
                                </div>
                                <div className="flex items-center justify-end gap-1.5 border-t border-slate-100 mt-3 pt-2">
                                  <button
                                    type="button"
                                    onClick={() => startEdit(clip)}
                                    className="inline-flex items-center justify-center p-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                                    title="Edit"
                                  >
                                    <FaEdit size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(clip.id)}
                                    className="inline-flex items-center justify-center p-1.5 text-xs font-semibold rounded-md bg-red-50 text-red-700 hover:bg-red-100 border border-red-100 transition-colors"
                                    title="Delete"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ===== VIEWER LIGHTBOX MODAL ===== */}
      {viewerClip && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 animate-fade-in"
          onClick={() => setViewerClip(null)}
        >
          <button
            type="button"
            onClick={() => setViewerClip(null)}
            className="absolute top-4 right-4 bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
          >
            <FaTimes size={24} />
          </button>

          <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
            >
              <FaMinus size={14} />
            </button>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
            >
              <FaPlus size={14} />
            </button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
            >
              <FaUndo size={14} />
            </button>
          </div>

          {/* Prev/Next arrows within the heading scope */}
          {getViewerImagesLength() > 1 && (
            <>
              <button
                type="button"
                onClick={prevViewerImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 text-white rounded-full p-3 hover:bg-white/30 transition-colors z-20"
                aria-label="Previous image"
              >
                <FaChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={nextViewerImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 text-white rounded-full p-3 hover:bg-white/30 transition-colors z-20"
                aria-label="Next image"
              >
                <FaChevronRight size={24} />
              </button>
            </>
          )}

          <div className="relative max-w-5xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="rounded-xl overflow-hidden bg-black flex flex-col">
              <div className="flex items-center justify-center p-3 overflow-auto bg-black max-h-[75vh]">
                <img
                  src={getViewerImageSrc()}
                  alt={viewerClip.heading}
                  className="max-h-[70vh] w-auto max-w-full object-contain"
                  style={{ transform: `scale(${zoom})`, transition: "transform 0.15s ease-out" }}
                />
              </div>
              <div className="bg-black/90 p-4 border-t border-slate-800 text-center">
                <p className="text-white text-lg font-bold">{viewerClip.heading}</p>
                <p className="text-slate-400 text-sm mt-1">
                  {formatDate(viewerClip.date)} 
                  {getViewerImagesLength() > 1 && (
                    <span className="ml-2 bg-slate-800 px-2 py-0.5 rounded text-xs text-white">
                      {viewerIndex + 1} / {getViewerImagesLength()}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCameraModal && (
        <CameraCaptureModal
          initialDestination="news"
          onClose={() => setShowCameraModal(false)}
          onSuccess={fetchClips}
        />
      )}

      <Footer />
    </>
  );
};

export default ManageNewsClips;
